import { Accordion } from '@mantine/core';
import { get } from 'lodash';
import { useMemo } from 'react';

import { Template, TemplateType } from '@e-origin/shared';

import { GeneralInfo } from './GeneralInfo';
import { HighValueSettings } from './HighValueSettings';
import { TemplateField } from './TemplateField';
import { TemplateFixedValue } from './TemplateFixedValue';

const replaceValueAtPath = (obj: Template, path: string, newValue: any) => {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  let current = obj;

  for (const key of keys) {
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  current[lastKey] = newValue;
  return obj;
};

const templateArrayBlocks = [
  { arrayPath: 'batchMappings.otherMappings', isFixedValue: false },
  { arrayPath: 'batchMappings.additionalFixedValues', isFixedValue: true },
  { arrayPath: 'highValueSettings.othersMappings', isFixedValue: false },
  { arrayPath: 'highValueSettings.shipmentsMapping.mappingFields', isFixedValue: false },
  { arrayPath: 'highValueSettings.stakeholdersMapping', isFixedValue: false },
  { arrayPath: 'additionalFixedValues', isFixedValue: true },
  { arrayPath: 'mappingFields', isFixedValue: false },
];

const getTemplateArrayBlock = (arrayPath) => templateArrayBlocks.find((block) => block.arrayPath === arrayPath);

export const useEditTemplateTabs = ({ form, editFields, editedTemplate, handleChange, editDisabled }) => {
  const handleTemplateFieldsChange = (changes, arrayPath) => {
    const newTemplateValue = replaceValueAtPath(editedTemplate, arrayPath, changes);
    handleChange(newTemplateValue);
  };

  const renderTemplateArrayField = (arrayPath, editDisabled, allowManualOrdering = false) => {
    const templateArrayBlock = getTemplateArrayBlock(arrayPath);
    if (templateArrayBlock.isFixedValue) {
      return (
        <TemplateFixedValue
          form={form}
          arrayPath={arrayPath}
          disabled={editDisabled}
          onChange={handleTemplateFieldsChange}
          propertyOptions={get(editFields, arrayPath)}
        />
      );
    } else {
      return (
        <TemplateField
          form={form}
          arrayPath={arrayPath}
          disabled={editDisabled}
          onChange={handleTemplateFieldsChange}
          propertyOptions={get(editFields, arrayPath)}
          allowManualOrdering={allowManualOrdering}
        />
      );
    }
  };

  const tabs = useMemo(
    () =>
      [
        {
          value: 'general-info',
          label: 'General info',
          component: <GeneralInfo templateType={editedTemplate?.type} handleChange={handleChange} form={form} editDisabled={editDisabled} />,
          show: true,
        },
        {
          value: 'additional-fixed-values',
          label: 'Additional fixed values',
          component: renderTemplateArrayField('additionalFixedValues', editDisabled),
          show: true,
        },
        {
          value: 'mapping-fields',
          label: 'Mapping fields',
          component: renderTemplateArrayField('mappingFields', editDisabled, true),
          show: ![TemplateType.ARRIVAL_TRANSIT, TemplateType.DEPARTURE_TRANSIT].includes(editedTemplate?.type),
        },
        {
          value: 'high-value-settings',
          label: 'High value settings',
          component: (
            <HighValueSettings
              renderTemplateArrayField={renderTemplateArrayField}
              handleChange={handleTemplateFieldsChange}
              form={form}
              editDisabled={editDisabled}
            />
          ),
          show: editedTemplate?.type === TemplateType.HIGH_VALUE_H1,
        },
        {
          value: 'batch-mappings',
          label: 'Batch mappings',
          component: (
            <Accordion variant="filled">
              <Accordion.Item key="otherMappings" value="otherMappings">
                <Accordion.Control>Other mappings</Accordion.Control>
                <Accordion.Panel>{renderTemplateArrayField('batchMappings.otherMappings', editDisabled)}</Accordion.Panel>
              </Accordion.Item>

              <Accordion.Item key="additionalFixedValues" value="additionalFixedValues">
                <Accordion.Control>Additional fixed values</Accordion.Control>
                <Accordion.Panel>{renderTemplateArrayField('batchMappings.additionalFixedValues', editDisabled)}</Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          ),
          show: editedTemplate?.type === TemplateType.HIGH_VALUE_H1,
        },
      ].filter((tab) => tab.show),
    [editedTemplate?.type, handleChange, form],
  );

  return { tabs };
};
